"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Compass, Layers, Sparkles, Maximize2, Paintbrush, Ruler, ShieldAlert, Zap, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
interface DecisionItem {
  title: string;
  description: string;
  icon: React.ElementType;
}
interface CostFactorItem {
  title: string;
  description: string;
  icon: React.ElementType;
}
const keyDecisions: DecisionItem[] = [{
  title: "Flooring choice",
  description: "Hardwood continuity, luxury vinyl plank, or natural stone transitions across living and utility spaces.",
  icon: Layers
}, {
  title: "Trim and architectural style",
  description: "Casing profiles, crown molding, modern flat stock, and custom baseboards establishing interior character.",
  icon: Compass
}, {
  title: "Lighting design and placement",
  description: "Recessed task lights, accent pendants, under-cabinet illumination, and multi-zone dimming controls.",
  icon: Sparkles
}, {
  title: "Wall removal for open concept",
  description: "Opening sightlines between kitchen, dining, and living zones for natural circulation and daylight.",
  icon: Maximize2
}, {
  title: "Paint and finish selection",
  description: "Cohesive color palettes, wall and ceiling sheens, durable trim coatings, and stain formulations.",
  icon: Paintbrush
}];
const costDrivers: CostFactorItem[] = [{
  title: "Square footage affected",
  description: "The total floor area, ceiling height, and subfloor prep required throughout the project perimeter.",
  icon: Ruler
}, {
  title: "Material grade and specification",
  description: "Custom cabinetry, counter slabs, artisan tile work, plumbing fixtures, and commercial-grade hardware selections.",
  icon: CheckCircle2
}, {
  title: "Structural wall removal complexity",
  description: "Engineering requirements, hidden utilities relocation, temporary shoring, and recessed steel or LVL beam installations.",
  icon: ShieldAlert
}, {
  title: "Electrical and lighting scope",
  description: "Panel capacity upgrades, circuit rerouting, smart switching systems, and newly installed fixture density.",
  icon: Zap
}];
export default function DecisionsFactors() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="remodelingdecisionsandcostfactors" data-nav="light" ref={ref} className="bg-background text-foreground py-20 lg:py-28 border-b border-border transition-opacity duration-700" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-14 lg:mb-20">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-foreground leading-tight">
            Key layout decisions and cost-driving factors
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Planning a major residential renovation requires balancing functional design choices with structural realities. Understanding these key variables early establishes clear priorities before work begins.
          </p>
        </div>

        {/* Two Main Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Column 1: Key Decisions */}
          <div className="flex flex-col gap-6">
            <div className="rounded-xl overflow-hidden border border-border bg-card shadow-xs">
              <div className="relative aspect-16/9 w-full bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/open_concept_living_remodel.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/open_concept_living_remodel.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/open_concept_living_remodel.png-wvc-sizes" alt="Bright open-concept kitchen and living room remodel with hardwood floors and recessed lighting" loading="lazy" className="w-full h-full object-cover" />
              </div>

              <Card className="border-0 shadow-none bg-transparent">
                <CardHeader className="pb-3">
                  <CardTitle className="text-xl sm:text-2xl font-serif text-foreground">
                    Layout and material decisions
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Fundamental aesthetic and spatial selections homeowners make during the architectural planning stage.
                  </p>
                </CardHeader>
                <CardContent className="pt-2">
                  <ul className="space-y-4" role="list">
                    {keyDecisions.map((decision, index) => {
                    const Icon = decision.icon;
                    return <li key={decision.title} data-index={index} className="flex items-start gap-3.5 p-3 rounded-lg hover:bg-muted/40 transition-colors border border-transparent hover:border-border/50">
                          <div className="p-2 rounded-md bg-primary/15 text-primary shrink-0 mt-0.5">
                            <Icon className="size-4" aria-hidden="true" />
                          </div>
                          <div>
                            <h3 className="text-base font-semibold text-foreground">
                              {decision.title}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-0.5 leading-normal">
                              {decision.description}
                            </p>
                          </div>
                        </li>;
                  })}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Column 2: Cost-Driving Factors */}
          <div className="flex flex-col gap-6">
            <div className="rounded-xl overflow-hidden border border-border bg-card shadow-xs">
              <div className="relative aspect-16/9 w-full bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/structural_wall_removal_framing.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/structural_wall_removal_framing.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/structural_wall_removal_framing.png-wvc-sizes" alt="Precision structural beam installation and architectural framing during residential remodeling" loading="lazy" className="w-full h-full object-cover" />
              </div>

              <Card className="border-0 shadow-none bg-transparent">
                <CardHeader className="pb-3">
                  <CardTitle className="text-xl sm:text-2xl font-serif text-foreground">
                    Cost-driving factors
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Technical variables and site specifications that directly shape the required investment and engineering scope.
                  </p>
                </CardHeader>
                <CardContent className="pt-2">
                  <ul className="space-y-4" role="list">
                    {costDrivers.map((factor, index) => {
                    const Icon = factor.icon;
                    return <li key={factor.title} data-index={index} className="flex items-start gap-3.5 p-3 rounded-lg hover:bg-muted/40 transition-colors border border-transparent hover:border-border/50">
                          <div className="p-2 rounded-md bg-secondary text-secondary-foreground shrink-0 mt-0.5">
                            <Icon className="size-4" aria-hidden="true" />
                          </div>
                          <div>
                            <h3 className="text-base font-semibold text-foreground">
                              {factor.title}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-0.5 leading-normal">
                              {factor.description}
                            </p>
                          </div>
                        </li>;
                  })}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Consult Callout */}
        <div className="mt-12 lg:mt-16 p-6 sm:p-8 rounded-xl bg-card border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 shadow-xs">
          <div className="max-w-2xl">
            <h3 className="text-lg sm:text-xl font-semibold text-foreground">
              Review your layout priorities with our team
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Every home presents unique structural conditions and finishing potentials. We evaluate your space in person to verify feasible configurations.
            </p>
          </div>
          <div className="shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/ContactRequestAnEstimatePage">
                <span>Request a consultation</span>
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}